import * as React from "react";
import styled from "styled-components";
import { useMappedState } from "redux-react-hook";

import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { IStore } from "src/core/shared/store";
import { configToS3BucketUrl } from "src/utils/aws";

import PaymentMethodCard, { PaymentMethodCardProps } from "./PaymentMethodCard";

export interface PaymentMethodCardGridListProps extends React.HTMLAttributes<HTMLDivElement> {
  loading?: boolean;
  cards?: PaymentMethodCardListProps[];
}

export interface PaymentMethodCardListProps extends PaymentMethodCardProps {
  uuid: string | number;
}

/**
 * Payment method card grid list
 */
const PaymentMethodCardGridList: React.ReactFCWithChildren<PaymentMethodCardGridListProps> = ({
  loading, cards, ...rest
}) => {
  const { awsConfig } = useMappedState(({ global }: IStore) => ({ awsConfig: global.settings?.aws }));
  const s3BucketBaseUrl = React.useMemo(() => configToS3BucketUrl(awsConfig), [ awsConfig ]);

  return (
    <StyledPaymentMethodCardGridList {...rest}>
      {loading ? (
        <LoadingSpinner type="circular" />
      ) : (
        <>
          {!cards || cards && cards.length === 0 ? (
            <Text
              className="no-payment-methods"
              fontSize="h4"
            >
              You have no payment methods
            </Text>
          ) : (
            <ul>
              {cards.map(card => (
                <li key={card.uuid}>
                  <PaymentMethodCard
                    s3BucketBaseUrl={s3BucketBaseUrl}
                    {...card}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </StyledPaymentMethodCardGridList>
  );
};

export default PaymentMethodCardGridList;

const StyledPaymentMethodCardGridList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  position: relative;
  min-height: 16.2rem;

  @media (min-width: 600px) {
    justify-content: flex-start;
  }

  .no-payment-methods {
    display: block;
    width: 100%;
    text-align: center;
  }

  ul {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    li {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: 2rem;
      width: 100%;

      @media (min-width: 600px) {
        padding-right: 2rem;
        width: auto;

        &:nth-child(2n+2) {
          padding-right: 0;
        }
      }

      @media (min-width: 768px) {
        &:nth-child(n+2) {
          padding-right: 2rem;
        }
      }
    }
  }
`;
